import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, ArrowRight, Smartphone, Server, AppWindow, Cpu, Monitor } from 'lucide-react';
import './ServicesSection.css';
import './ServicesSection.motion.css';
import useInViewOnce from './hooks/useInViewOnce';
import useCardTilt from './hooks/useCardTilt';
import { REDUCED_MOTION_QUERY } from './hooks/useMotionPrefs';

import illMobile from './images/design/ill-mobile.svg';
import illBackend from './images/design/ill-backend.svg';
import illWeb from './images/design/ill-web.svg';
import illAi from './images/design/ill-ai.svg';
import illDesktop from './images/design/ill-desktop.svg';

const services = [
    {
        key: 'mobile',
        Icon: Smartphone,
        color: '16, 185, 129',
        illustration: illMobile,
        illustrationClass: 'service-ill--mobile',
        tags: ['Kotlin', 'Flutter', 'Jetpack'],
        filter: 'Mobile',
    },
    {
        key: 'backend',
        Icon: Server,
        color: '59, 130, 246',
        illustration: illBackend,
        tags: ['Spring Boot', 'PostgreSQL', 'Kafka'],
        filter: 'Backend',
    },
    {
        key: 'web',
        Icon: AppWindow,
        color: '249, 115, 22',
        illustration: illWeb,
        tags: ['Angular', 'React', 'TypeScript'],
        filter: 'Web',
    },
    {
        key: 'ai',
        Icon: Cpu,
        color: '139, 92, 246',
        illustration: illAi,
        tags: ['Python', 'LangGraph', 'FastAPI'],
        filter: 'AI',
    },
    {
        key: 'desktop',
        Icon: Monitor,
        color: '99, 102, 241',
        illustration: illDesktop,
        tags: ['.NET', 'WPF', 'JavaFX'],
        filter: 'Desktop',
    },
];

const easeInOutCubic = (p) => (p < 0.5 ? 4 * p * p * p : 1 - ((-2 * p + 2) ** 3) / 2);

// Eased horizontal scroll (scroll-snap is paused during the glide, then restored).
// Returns a cancel function; falls back to an instant jump for reduced motion.
const glideScroll = (el, delta, duration = 620) => {
    const reduced = window.matchMedia && window.matchMedia(REDUCED_MOTION_QUERY).matches;
    const max = el.scrollWidth - el.clientWidth;
    const start = el.scrollLeft;
    // scrollLeft runs 0..max in LTR and -max..0 in RTL
    const min = start <= 0 && getComputedStyle(el).direction === 'rtl' ? -max : 0;
    const upper = min < 0 ? 0 : max;
    const target = Math.max(min, Math.min(upper, start + delta));
    if (reduced || target === start) {
        el.scrollLeft = target;
        return () => {};
    }

    const snap = el.style.scrollSnapType;
    el.style.scrollSnapType = 'none';
    let frame = 0;
    let t0 = null;
    const finish = () => {
        cancelAnimationFrame(frame);
        el.style.scrollSnapType = snap;
    };
    const step = (now) => {
        if (t0 === null) t0 = now;
        const p = Math.min(1, (now - t0) / duration);
        el.scrollLeft = start + (target - start) * easeInOutCubic(p);
        if (p < 1) frame = requestAnimationFrame(step);
        else finish();
    };
    frame = requestAnimationFrame(step);
    return finish;
};

const ServicesSection = ({ onSelectCategory }) => {
    const { t, i18n } = useTranslation();
    const sectionRef = useRef(null);
    const trackRef = useRef(null);
    const cancelGlide = useRef(() => {});
    useInViewOnce(sectionRef);
    useCardTilt(trackRef);
    const [canPrev, setCanPrev] = useState(false);
    const [canNext, setCanNext] = useState(false);
    const isRtl = i18n.language === 'ar';

    const updateArrows = useCallback(() => {
        const el = trackRef.current;
        if (!el) return;
        const max = el.scrollWidth - el.clientWidth;
        const pos = Math.abs(el.scrollLeft); // scrollLeft is negative in RTL
        setCanPrev(pos > 2);
        setCanNext(pos < max - 2);
    }, []);

    useEffect(() => {
        const el = trackRef.current;
        if (!el) return undefined;
        updateArrows();
        el.addEventListener('scroll', updateArrows, { passive: true });
        const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(updateArrows);
        if (observer) observer.observe(el);
        return () => {
            el.removeEventListener('scroll', updateArrows);
            if (observer) observer.disconnect();
        };
    }, [updateArrows, isRtl]);

    const scrollByCard = (direction) => {
        const el = trackRef.current;
        if (!el) return;
        const card = el.querySelector('.service-card');
        const step = card ? card.offsetWidth + 16 : 240;
        cancelGlide.current();
        cancelGlide.current = glideScroll(el, direction * step * (isRtl ? -1 : 1));
    };

    // A wheel / touch / drag takes over from an in-flight arrow glide
    useEffect(() => {
        const el = trackRef.current;
        if (!el) return undefined;
        const stop = () => cancelGlide.current();
        el.addEventListener('wheel', stop, { passive: true });
        el.addEventListener('pointerdown', stop, { passive: true });
        return () => {
            stop();
            el.removeEventListener('wheel', stop);
            el.removeEventListener('pointerdown', stop);
        };
    }, []);

    return (
        <section className="section services" id="Services" ref={sectionRef}>
            <div className="container">
                <div className="section-header-row svc-header" data-inview>
                    <div className="section-header">
                        <span className="eyebrow">{t('services.eyebrow')}</span>
                        <h2 className="section-title">{t('services.title')}</h2>
                        <p className="section-subtitle">{t('services.subtitle')}</p>
                    </div>
                    <div className="services-actions">
                        <button
                            className="circle-btn"
                            onClick={() => scrollByCard(-1)}
                            disabled={!canPrev}
                            aria-label={t('services.prev')}
                        >
                            <ArrowLeft size={15} className="flip-rtl" />
                        </button>
                        <button
                            className="circle-btn"
                            onClick={() => scrollByCard(1)}
                            disabled={!canNext}
                            aria-label={t('services.next')}
                        >
                            <ArrowRight size={15} className="flip-rtl" />
                        </button>
                    </div>
                </div>

                <div className="services-track" ref={trackRef} data-inview>
                    {services.map(({ key, Icon, color, illustration, illustrationClass, tags, filter }, index) => (
                        <article
                            key={key}
                            className="service-card"
                            style={{ '--accent': color, '--i': index }}
                            data-inview
                        >
                            <div className="service-top">
                                <span className="icon-tile service-icon">
                                    <Icon size={20} />
                                </span>
                                <span className="service-ill-box svc-float">
                                    <img src={illustration} alt="" className={`service-ill ${illustrationClass || ''}`} />
                                </span>
                            </div>
                            <h3 className="service-title">{t(`services.items.${key}.title`)}</h3>
                            <p className="service-description">{t(`services.items.${key}.description`)}</p>
                            <div className="service-footer">
                                <div className="service-tags">
                                    {tags.map(tag => <span key={tag} className="tag">{tag}</span>)}
                                </div>
                                <a
                                    href="#Projects"
                                    className="circle-btn circle-btn-sm"
                                    onClick={() => onSelectCategory(filter)}
                                    aria-label={t('services.see_projects', { name: t(`services.items.${key}.title`) })}
                                    title={t('services.see_projects', { name: t(`services.items.${key}.title`) })}
                                >
                                    <ArrowRight size={15} className="flip-rtl" />
                                </a>
                            </div>
                        </article>
                    ))}
                </div>
            </div>
        </section>
    );
};

export default ServicesSection;
